// Core data types for Planogram Plan Template Management

// Plan type enumeration
export type PlanType = 
  | 'Major Plans'     // Large-scale changes
  | 'Regular Plans'   // Regular adjustments
  | 'Minor Plans'     // Small-scale adjustments
  | 'Urgent Plans'    // Emergency changes
  | 'Special Plans';  // Special purpose plans

// Template status
export type TemplateStatus = 'Draft' | 'Published' | 'Disabled';



// Responsible team enumeration
export type ResponsibleTeam = 
  | 'Planogram Team'
  | 'Construction Team'
  | 'Commercial Team'
  | 'Marketing Team'
  | 'Operation Team'
  | 'IT Team'
  | 'System Auto';

// Cycle frequency types
export type CycleFrequencyType = 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';

// Termination types
export type TerminationType = 'Infinite Repeat' | 'End on Date' | 'Limited Count';

// Cycle status
export type CycleStatus = 'Active' | 'Paused' | 'Terminated';

// Scope types
export type ScopeType = 
  | 'All Stores'
  | 'Specific Store Groups'
  | 'Custom Store Collections'
  | 'Individual Stores'
  | 'Specific Stores';

// Validation status
export type ValidationStatus = 'Not Validated' | 'Validation Passed' | 'Validation Failed';

// Milestone configuration
export interface MilestoneConfig {
  id: string;
  code: string;                        // Milestone code
  name: string;                        // Milestone name
  description: string;                 // Milestone description
  sequence: number;                    // Execution order (1-9)
  responsibleTeam: ResponsibleTeam;    // Team responsible
  prerequisites: string[];             // Prerequisites
  deliverables: string[];              // Expected deliverables
  durationEstimate: number;            // Estimated duration (days)
  bufferTime: number;                  // Buffer time (days)
  parallelExecution: boolean;          // Can execute in parallel
  isFinalMilestone: boolean;           // Is final milestone flag
}

// Store selection criteria
export interface SelectionCriteria {
  areas?: string[];                   // Regional filters
  formats?: string[];                 // Store format filters
  regions?: string[];                 // Region filters
  storeTypes?: string[];              // Store type filters
}

// Scope configuration
export interface ScopeConfig {
  scopeType: ScopeType;                // All/Groups/Custom/Individual
  storeGroupList: string[];            // Selected store group IDs
  customStoreList: string[];           // Custom store IDs
  storeDisplayGroupList?: any[];    // 新增：门店陈列分组列表
  storeSelectionCriteria: SelectionCriteria; // Selection criteria
  excludedStores: string[];            // Excluded store IDs
  storeCount: number;                  // Total affected stores
  scopeValidationStatus: ValidationStatus; // Validation status
  lastScopeUpdate: number;             // Last update timestamp (UTC milliseconds)
}

// Cycle configuration
export interface CycleConfig {
  frequencyType: CycleFrequencyType;   // Daily/Weekly/Monthly/Yearly
  interval: number;                    // Interval value
  weekDaySelection?: number[];         // Selected weekdays (0-6)
  monthDaySelection?: number[];        // Selected month days (1-31)
  firstExecutionDate: number;          // First execution timestamp (UTC milliseconds)
  terminationType: TerminationType;    // Termination type
  endDate?: number;                    // End timestamp (if applicable, UTC milliseconds)
  repeatCount?: number;                // Repeat count (if applicable)
  cycleStatus: CycleStatus;            // Active/Paused/Terminated
  lastGenerationTime?: number;         // Last generation timestamp (UTC milliseconds)
  nextGenerationTime?: number;         // Next generation timestamp (UTC milliseconds)
}

// Performance metrics
export interface PerformanceMetrics {
  averageExecutionTime: number;        // Average completion time
  onTimeCompletionRate: number;        // On-time completion rate
  resourceUtilization: number;         // Resource utilization rate
  errorRate: number;                   // Error occurrence rate
}

// Usage statistics
export interface UsageStatistics {
  usageCount: number;                  // Times used
  activePlans: number;                 // Currently active plans
  lastUsedTime?: number;               // Last usage timestamp (UTC milliseconds)
  usageFeedback: string[];             // User feedback
  performanceMetrics: PerformanceMetrics; // Performance data
}

// Main template entity
export interface PlanogramTemplate {
  id: string;
  code: string;                        // Unique template code
  name: string;                        // Template display name
  planType: PlanType;                  // Major/Regular/Minor/Urgent/Special
  description: string;                 // Template description
  businessActivity: string;            // Associated business activity
  owner: string;                       // Template owner user ID
  ownerName: string;                       // Template owner user ID
  status: TemplateStatus;              // Draft/Published/Disabled
  milestones: MilestoneConfig[];       // Milestone configuration
  executionCycle: CycleConfig;         // Execution cycle settings
  scopeDefinition: ScopeConfig;        // Impact scope configuration
  validationStatus: ValidationStatus; // Validation state
  usageStatistics: UsageStatistics;    // Usage metrics
  createTime: number;                  // Creation timestamp (UTC milliseconds)
  updateTime: number;                  // Last update timestamp (UTC milliseconds)
  creator: string;                     // Creator user ID
  remark?: string;                     // Additional notes
}

// Filter parameters
export interface TemplateFilterParams {
  searchText?: string;                 // Search by name/code
  planType?: PlanType | 'all';         // Plan type filter
  status?: TemplateStatus | 'all';     // Status filter
  creator?: string;                    // Creator filter
  createTimeStart?: number;            // Creation date range start (UTC milliseconds)
  createTimeEnd?: number;              // Creation date range end (UTC milliseconds)
  businessActivity?: string;           // Business activity filter
  nextGenerationTimeStart?: number;    // Next generation time range start (UTC milliseconds)
  nextGenerationTimeEnd?: number;      // Next generation time range end (UTC milliseconds)
}

// Store group information
export interface StoreGroup {
  id: string;
  code: string;
  name: string;
  displayGrouopCode?: string;
  description?: string;
  shopCodes: string[];
}

// Individual store information
export interface Store {
  id: string;
  code: string;
  name: string;
  area: string;
  format: string;
  clusterSection: string;
  detailAddress: string;
}

// Milestone type definition
export interface MilestoneType {
  id: string;
  code: string;
  name: string;
  description: string;
  defaultResponsibleTeam: ResponsibleTeam;
  defaultDuration: number;
  defaultBufferTime: number;
  canBeParallel: boolean;
  isFinalMilestone: boolean;
  category: MilestoneCategory;
  prerequisites?: string[];
  deliverables: string[];
  estimatedCost?: number;
  requiredSkills?: string[];
  riskLevel: 'Low' | 'Medium' | 'High';
}

// Milestone category enumeration (kept for reference only)
export type MilestoneCategory = 
  | 'Planning'      // 规划阶段
  | 'Design'        // 设计阶段
  | 'Preparation'   // 准备阶段
  | 'Execution'     // 执行阶段
  | 'Review'        // 审核阶段
  | 'Completion';   // 完成阶段

// Milestone business rules
export const MILESTONE_BUSINESS_RULES: BusinessRule[] = [
  // {
  //   id: 'MBR001',
  //   name: 'Final Milestone Rule',
  //   description: '最后一个里程碑必须是Store Task Generation',
  //   category: 'milestone',
  //   severity: 'error',
  //   validator: (milestones: MilestoneConfig[]) => {
  //     const sortedMilestones = [...milestones].sort((a, b) => a.sequence - b.sequence);
  //     const lastMilestone = sortedMilestones[sortedMilestones.length - 1];
  //     return {
  //       isValid: lastMilestone?.name === 'Store Task Generation' && lastMilestone?.isFinalMilestone,
  //       message: '最后一个里程碑必须是"Store Task Generation"'
  //     };
  //   }
  // },
  {
    id: 'MBR002',
    name: 'Sequence Uniqueness Rule',
    description: '里程碑序号必须唯一',
    category: 'milestone',
    severity: 'error',
    validator: (milestones: MilestoneConfig[]) => {
      const sequences = milestones.map(m => m.sequence);
      const uniqueSequences = new Set(sequences);
      return {
        isValid: sequences.length === uniqueSequences.size,
        message: '里程碑序号不能重复'
      };
    }
  },
  {
    id: 'MBR003',
    name: 'Duration Validation Rule',
    description: '里程碑工期必须合理',
    category: 'milestone',
    severity: 'warning',
    validator: (milestone: MilestoneConfig) => {
      const totalTime = milestone.durationEstimate + milestone.bufferTime;
      return {
        isValid: totalTime <= 60,
        message: totalTime > 60 ? '里程碑总时长超过60天，请确认是否合理' : undefined
      };
    }
  },
  {
    id: 'MBR004',
    name: 'Parallel Execution Rule',
    description: '并行执行的里程碑必须有相同的负责团队或兼容团队',
    category: 'milestone',
    severity: 'warning',
    validator: (data: any) => {
      const milestone = data as MilestoneConfig;
      const allMilestones = data.allMilestones as MilestoneConfig[] || [];
      if (!milestone.parallelExecution) return { isValid: true };
      
      const parallelMilestones = allMilestones.filter(m => 
        m.parallelExecution && m.sequence === milestone.sequence && m.id !== milestone.id
      );
      
      if (parallelMilestones.length === 0) return { isValid: true };
      
      const compatibleTeams = ['Planogram Team', 'System Auto'];
      const isCompatible = parallelMilestones.every(m => 
        m.responsibleTeam === milestone.responsibleTeam || 
        compatibleTeams.includes(m.responsibleTeam)
      );
      
      return {
        isValid: isCompatible,
        message: '并行执行的里程碑建议使用兼容的负责团队'
      };
    }
  }
];

// Default milestone configurations by plan type - Different plan types use different milestone combinations
export const DEFAULT_MILESTONE_CONFIGS: Record<PlanType, Partial<MilestoneConfig>[]> = {
  'Major Plans': [
    // 大型计划：完整的9个里程碑
    {
      code: 'DISPLAY_GROUP_ADJUSTMENT',
      name: 'Display Group Adjustment',
      description: 'Adjust Sub-Class associations of HQ Display Group, affecting product grouping logic',
      sequence: 1,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 5,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'STORE_GROUP_SETUP',
      name: 'Store Group Setup',
      description: 'Adjust store scope under Store Group, determining stores covered by the plan',
      sequence: 2,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 3,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'BENCHMARK_MASTER_DRAWING',
      name: 'Benchmark Master Drawing',
      description: 'Draw Benchmark drawings in Nielsen software based on Store Group',
      sequence: 3,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 7,
      bufferTime: 3,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'STORE_LAYOUT_ADJUSTMENT',
      name: 'Store Layout Adjustment',
      description: 'Execute physical layout adjustments in stores',
      sequence: 4,
      responsibleTeam: 'Construction Team',
      durationEstimate: 10,
      bufferTime: 3,
      parallelExecution: true,
      isFinalMilestone: false
    },
    {
      code: 'FIXTURE_TYPE_SETUP',
      name: 'Fixture Type Set Up',
      description: 'Set up new fixture types in master data for use in display planning',
      sequence: 5,
      responsibleTeam: 'Construction Team',
      durationEstimate: 5,
      bufferTime: 2,
      parallelExecution: true,
      isFinalMilestone: false
    },
    {
      code: 'DISPLAY_PLANNING_ADJUSTMENT',
      name: 'Display Planning Adjustment',
      description: 'Adjust section numbering and Display Group associations',
      sequence: 6,
      responsibleTeam: 'Construction Team',
      durationEstimate: 6,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'FIXTURE_PROCUREMENT',
      name: 'Fixture Procurement',
      description: 'Generate fixture procurement requirements based on layout and display adjustments',
      sequence: 7,
      responsibleTeam: 'Construction Team',
      durationEstimate: 8,
      bufferTime: 3,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'DISPLAY_DIAGRAM_GENERATION',
      name: 'Algorithm Generated Planogram for Review',
      description: 'Generate store display diagrams at specified times and conduct review confirmation',
      sequence: 8,
      responsibleTeam: 'System Auto',
      durationEstimate: 4,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: true
    },
    {
      code: 'STORE_TASK_GENERATION',
      name: 'Store Task Generation',
      description: 'Generate final store display adjustment tasks and distribute for execution',
      sequence: 9,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true
    }
  ],
  'Regular Plans': [
    // 常规计划：6个里程碑
    {
      code: 'BENCHMARK_MASTER_DRAWING',
      name: 'Benchmark Master Drawing',
      description: 'Draw Benchmark drawings in Nielsen software based on Store Group',
      sequence: 1,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 4,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'STORE_LAYOUT_ADJUSTMENT',
      name: 'Store Layout Adjustment',
      description: 'Execute physical layout adjustments in stores',
      sequence: 2,
      responsibleTeam: 'Construction Team',
      durationEstimate: 6,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'DISPLAY_PLANNING_ADJUSTMENT',
      name: 'Display Planning Adjustment',
      description: 'Adjust section numbering and Display Group associations',
      sequence: 3,
      responsibleTeam: 'Construction Team',
      durationEstimate: 4,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'FIXTURE_PROCUREMENT',
      name: 'Fixture Procurement',
      description: 'Generate fixture procurement requirements based on layout and display adjustments',
      sequence: 4,
      responsibleTeam: 'Construction Team',
      durationEstimate: 5,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'DISPLAY_DIAGRAM_GENERATION',
      name: 'Algorithm Generated Planogram for Review',
      description: 'Generate store display diagrams at specified times and conduct review confirmation',
      sequence: 5,
      responsibleTeam: 'System Auto',
      durationEstimate: 3,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: true
    },
    {
      code: 'STORE_TASK_GENERATION',
      name: 'Store Task Generation',
      description: 'Generate final store display adjustment tasks and distribute for execution',
      sequence: 6,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true
    }
  ],
  'Minor Plans': [
    // 小型计划：4个里程碑
    {
      code: 'DISPLAY_PLANNING_ADJUSTMENT',
      name: 'Display Planning Adjustment',
      description: 'Adjust section numbering and Display Group associations',
      sequence: 1,
      responsibleTeam: 'Construction Team',
      durationEstimate: 2,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'FIXTURE_PROCUREMENT',
      name: 'Fixture Procurement',
      description: 'Generate fixture procurement requirements based on layout and display adjustments',
      sequence: 2,
      responsibleTeam: 'Construction Team',
      durationEstimate: 3,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'DISPLAY_DIAGRAM_GENERATION',
      name: 'Algorithm Generated Planogram for Review',
      description: 'Generate store display diagrams at specified times and conduct review confirmation',
      sequence: 3,
      responsibleTeam: 'System Auto',
      durationEstimate: 2,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: true
    },
    {
      code: 'STORE_TASK_GENERATION',
      name: 'Store Task Generation',
      description: 'Generate final store display adjustment tasks and distribute for execution',
      sequence: 4,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true
    }
  ],
  'Urgent Plans': [
    // 紧急计划：完整的9个里程碑，但时间压缩
    {
      code: 'DISPLAY_GROUP_ADJUSTMENT',
      name: 'Display Group Adjustment',
      description: 'Adjust Sub-Class associations of HQ Display Group, affecting product grouping logic',
      sequence: 1,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'STORE_GROUP_SETUP',
      name: 'Store Group Setup',
      description: 'Adjust store scope under Store Group, determining stores covered by the plan',
      sequence: 2,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'BENCHMARK_MASTER_DRAWING',
      name: 'Benchmark Master Drawing',
      description: 'Draw Benchmark drawings in Nielsen software based on Store Group',
      sequence: 3,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 2,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'STORE_LAYOUT_ADJUSTMENT',
      name: 'Store Layout Adjustment',
      description: 'Execute physical layout adjustments in stores',
      sequence: 4,
      responsibleTeam: 'Construction Team',
      durationEstimate: 3,
      bufferTime: 1,
      parallelExecution: true,
      isFinalMilestone: false
    },
    {
      code: 'FIXTURE_TYPE_SETUP',
      name: 'Fixture Type Set Up',
      description: 'Set up new fixture types in master data for use in display planning',
      sequence: 5,
      responsibleTeam: 'Construction Team',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: true,
      isFinalMilestone: false
    },
    {
      code: 'DISPLAY_PLANNING_ADJUSTMENT',
      name: 'Display Planning Adjustment',
      description: 'Adjust section numbering and Display Group associations',
      sequence: 6,
      responsibleTeam: 'Construction Team',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'FIXTURE_PROCUREMENT',
      name: 'Fixture Procurement',
      description: 'Generate fixture procurement requirements based on layout and display adjustments',
      sequence: 7,
      responsibleTeam: 'Construction Team',
      durationEstimate: 2,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'DISPLAY_DIAGRAM_GENERATION',
      name: 'Algorithm Generated Planogram for Review',
      description: 'Generate store display diagrams at specified times and conduct review confirmation',
      sequence: 8,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true
    },
    {
      code: 'STORE_TASK_GENERATION',
      name: 'Store Task Generation',
      description: 'Generate final store display adjustment tasks and distribute for execution',
      sequence: 9,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true
    }
  ],
  'Special Plans': [
    // 特殊计划：完整的9个里程碑
    {
      code: 'DISPLAY_GROUP_ADJUSTMENT',
      name: 'Display Group Adjustment',
      description: 'Adjust Sub-Class associations of HQ Display Group, affecting product grouping logic',
      sequence: 1,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 4,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'STORE_GROUP_SETUP',
      name: 'Store Group Setup',
      description: 'Adjust store scope under Store Group, determining stores covered by the plan',
      sequence: 2,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 2,
      bufferTime: 1,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'BENCHMARK_MASTER_DRAWING',
      name: 'Benchmark Master Drawing',
      description: 'Draw Benchmark drawings in Nielsen software based on Store Group',
      sequence: 3,
      responsibleTeam: 'Planogram Team',
      durationEstimate: 6,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'STORE_LAYOUT_ADJUSTMENT',
      name: 'Store Layout Adjustment',
      description: 'Execute physical layout adjustments in stores',
      sequence: 4,
      responsibleTeam: 'Construction Team',
      durationEstimate: 8,
      bufferTime: 3,
      parallelExecution: true,
      isFinalMilestone: false
    },
    {
      code: 'FIXTURE_TYPE_SETUP',
      name: 'Fixture Type Set Up',
      description: 'Set up new fixture types in master data for use in display planning',
      sequence: 5,
      responsibleTeam: 'Construction Team',
      durationEstimate: 4,
      bufferTime: 2,
      parallelExecution: true,
      isFinalMilestone: false
    },
    {
      code: 'DISPLAY_PLANNING_ADJUSTMENT',
      name: 'Display Planning Adjustment',
      description: 'Adjust section numbering and Display Group associations',
      sequence: 6,
      responsibleTeam: 'Construction Team',
      durationEstimate: 5,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'FIXTURE_PROCUREMENT',
      name: 'Fixture Procurement',
      description: 'Generate fixture procurement requirements based on layout and display adjustments',
      sequence: 7,
      responsibleTeam: 'Construction Team',
      durationEstimate: 7,
      bufferTime: 3,
      parallelExecution: false,
      isFinalMilestone: false
    },
    {
      code: 'DISPLAY_DIAGRAM_GENERATION',
      name: 'Algorithm Generated Planogram for Review',
      description: 'Generate store display diagrams at specified times and conduct review confirmation',
      sequence: 8,
      responsibleTeam: 'System Auto',
      durationEstimate: 3,
      bufferTime: 2,
      parallelExecution: false,
      isFinalMilestone: true
    },
    {
      code: 'STORE_TASK_GENERATION',
      name: 'Store Task Generation',
      description: 'Generate final store display adjustment tasks and distribute for execution',
      sequence: 9,
      responsibleTeam: 'System Auto',
      durationEstimate: 1,
      bufferTime: 0,
      parallelExecution: false,
      isFinalMilestone: true
    }
  ]
};

// Pagination parameters
export interface PaginationParams {
  current: number;
  pageSize: number;
}

// Paginated response
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  current: number;
  pageSize: number;
  totalPages: number;
}

// API response wrapper - 符合前端开发标准的统一响应格式
export interface ApiResponse<T> {
  traceId: string;
  success: boolean;
  errorMessage: string | null;
  flag: 'success' | 'fail';
  data: T;
  msg?: string | null;
  errorCode?: string | null;
  code?: string | null;
  message?: string | null;
}

// Error state
export interface ErrorState {
  hasError: boolean;
  message: string;
  details?: string;
  errorCode?: string;
}

// Validation error
export interface ValidationError {
  field: string;
  message: string;
  type: 'error' | 'warning';
  code?: string;
}

// Validation result
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings?: ValidationError[];
}

// Batch operation result
export interface BatchOperationResult {
  successCount: number;
  failureCount: number;
  errors: Array<{
    id: string;
    error: string;
  }>;
}


// Plan preview
export interface PlanPreview {
  id: string;
  name: string;
  scheduledDate: string;
  estimatedDuration: number;
  affectedStores: number;
  milestoneCount: number;
}

// Create template request
export interface CreateTemplateRequest {
  code: string;
  name: string;
  planType: PlanType;
  description: string;
  businessActivity: string;
  owner: string;
  milestones: MilestoneConfig[];
  executionCycle: CycleConfig;
  scopeDefinition: ScopeConfig;
  remark?: string;
}

// Update template request
export interface UpdateTemplateRequest extends Partial<CreateTemplateRequest> {
  id: string;
}

// Store filter parameters
export interface StoreFilterParams {
  searchText?: string;
  areas?: string[];
  formats?: string[];
  clusterSections?: string[];
}

// Wizard step type
export type WizardStep = 
  | 'planType'
  | 'basicInfo'
  | 'milestones'
  | 'cycle'
  | 'scope'
  | 'validation';

// Wizard state
export interface WizardState {
  currentStep: WizardStep;
  completedSteps: WizardStep[];
  templateData: Partial<PlanogramTemplate>;
  isValid: boolean;
  errors: ValidationError[];
}

// Validation schemas and rules
export interface ValidationRule {
  field: string;
  required?: boolean;
  type?: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'date';
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: RegExp;
  custom?: (value: any, data?: any) => string | null;
  message?: string;
}

export interface ValidationSchema {
  rules: ValidationRule[];
  dependencies?: Record<string, string[]>;
}


// Template code uniqueness check parameters
export interface CodeUniquenessCheckParams {
  code: string;
  excludeId?: string;
}

// Business rule definitions
export interface BusinessRule {
  id: string;
  name: string;
  description: string;
  category: 'template' | 'milestone' | 'cycle' | 'scope';
  severity: 'error' | 'warning' | 'info';
  validator: (data: any) => { isValid: boolean; message?: string };
}